import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Calendar, MapPin, Users, ArrowRight, Search, Filter, Mic } from 'lucide-react'
import CinematicNav, { BackToTop } from '../../components/landing/CinematicNav'
import CinematicFooter from '../../components/landing/CinematicFooter'
import Reveal from '../../components/landing/Reveal'
import Breadcrumbs from '../../components/masterclass/Breadcrumbs'
import { resolveImageUrl } from '../../utils/imageUrl'
import { SCHEDULE_SERIES_NAME } from '../../utils/masterclassSchedule'
import { masterclassService } from '../../services/masterclassService'

const CATEGORIES = [
  'All', 'Photography', 'Film', 'Graphic Design', 'Motion Graphics',
  'AI', 'Marketing', 'Business', 'Entrepreneurship',
]

function formatDate(d) {
  return d && !isNaN(new Date(d).getTime()) ? new Date(d).toLocaleDateString('en-UG', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }) : ''
}

function ArchiveCard({ session }) {
  const speaker = session.speaker_name || 'TBA'
  const photo = resolveImageUrl(session.speaker_photo) || '/Tutors/default.jpg'

  return (
    <Link to={`/masterclass/${session.slug || session.id}`} className="group block h-full border-b border-white/5 pb-6">
      <div className="h-full flex flex-col">
        <div className="relative aspect-[16/10] overflow-hidden mb-5">
          {session.banner_url ? (
            <img src={resolveImageUrl(session.banner_url)} alt={session.topic} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          ) : (
            <div className="w-full h-full bg-cinema-graphite flex items-center justify-center">
              <Mic className="w-12 h-12 text-cinema-muted/20" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-cinema-black via-cinema-black/40 to-transparent" />
          <div className="absolute top-4 left-4">
            <span className="cinema-label bg-cinema-black/60 px-3 py-1.5">{session.category || 'General'}</span>
          </div>
          <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
            <img src={photo} alt={speaker} className="w-12 h-12 rounded-full object-cover border-2 border-cinema-gold/30" loading="lazy" />
            <div>
              <p className="text-white font-sans text-sm font-medium">{speaker}</p>
              <p className="text-cinema-muted text-xs">{session.speaker_title}{session.speaker_company ? ` · ${session.speaker_company}` : ''}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4 flex-1">
          <h3 className="font-display text-xl text-white font-medium leading-tight group-hover:text-cinema-gold transition-colors">
            {session.topic}
          </h3>
          <p className="text-cinema-muted font-sans text-sm line-clamp-2">
            {session.summary || session.description || `A ${SCHEDULE_SERIES_NAME} session at Synergy Multimedia Academy.`}
          </p>
          <div className="flex flex-wrap gap-4 text-cinema-muted font-sans text-xs">
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-cinema-gold" /> {formatDate(session.event_date)}</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-cinema-gold" /> {session.venue || 'SMA, Kampala'}</span>
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-cinema-gold" /> {session.attendees_count || session.registration_count || 0} attendees</span>
          </div>
          <div className="pt-2 flex items-center gap-2 text-cinema-gold text-sm font-medium">
            View Details <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </div>
    </Link>
  )
}

export default function ArchivePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [sessions, setSessions] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'All')
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [showFilters, setShowFilters] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)

  useEffect(() => {
    document.title = `Masterclass Archive | ${SCHEDULE_SERIES_NAME}`
    const handleScroll = () => {
      setScrolled(window.scrollY > 40)
      setShowBackToTop(window.scrollY > 800)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setLoading(true)
    Promise.all([
      masterclassService.getArchive({
        category: activeCategory === 'All' ? '' : activeCategory,
        search,
        limit: 100,
      }),
      masterclassService.getCategories(),
    ]).then(([archive, cats]) => {
      setSessions(archive.sessions || [])
      setCategories(cats.categories || [])
    }).catch(() => {
      setSessions([])
      setCategories([])
    }).finally(() => setLoading(false))
  }, [activeCategory, search])

  useEffect(() => {
    const params = {}
    if (activeCategory !== 'All') params.category = activeCategory
    if (search) params.search = search
    setSearchParams(params, { replace: true })
  }, [activeCategory, search])

  const categoryList = categories.length ? ['All', ...categories] : CATEGORIES

  return (
    <div className="cinema-page min-h-screen bg-cinema-black">
      <div className="cinema-grain" aria-hidden="true" />
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="cinema-glow-orb w-[700px] h-[700px] -top-40 left-1/2 -translate-x-1/2 opacity-30" />
        <div className="cinema-container relative">
          <Reveal>
            <Breadcrumbs />
          </Reveal>
          <Reveal delay={100}>
            <span className="cinema-label mt-10 block">The Creative Talk Series</span>
            <h1 className="cinema-headline-lg mt-6">
              Masterclass <span className="text-cinema-gold">Archive</span>
            </h1>
            <p className="cinema-body-lg mt-6 max-w-2xl text-cinema-muted">
              Every session, every speaker, every lesson — documented for the creative community.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-0 z-30 bg-cinema-black/90 backdrop-blur-md border-b border-white/5 py-4">
        <div className="cinema-container">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0 no-scrollbar">
              {categoryList.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`whitespace-nowrap px-4 py-2 text-xs font-sans uppercase tracking-wider transition-all ${
                    activeCategory === cat
                      ? 'text-cinema-glow'
                      : 'text-cinema-muted hover:text-cinema-gold'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="flex-1" />

            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted" />
                <input
                  type="text"
                  placeholder="Search speakers, topics..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full lg:w-64 bg-cinema-graphite border-b border-white/10 text-white pl-10 pr-4 py-2.5 text-sm font-sans focus:outline-none focus:border-cinema-gold/40 transition-colors"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2.5 text-cinema-muted text-sm font-sans hover:text-cinema-gold transition-colors"
              >
                <Filter className="w-4 h-4" /> Filter
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-cinema-charcoal py-24 sm:py-32">
        <div className="cinema-container">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-cinema-graphite/50 h-80 animate-pulse" />
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-24">
              <div className="text-6xl mb-6">📚</div>
              <h3 className="font-display text-2xl text-white">No sessions found</h3>
              <p className="text-cinema-muted font-sans mt-3">Check back soon as we archive more masterclasses.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {sessions.map((session, i) => (
                <Reveal key={session.id} delay={i * 60}>
                  <ArchiveCard session={session} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CinematicFooter contactEmail="synergymultimediaacademy@gmail.com" />
      <BackToTop visible={showBackToTop} />
    </div>
  )
}
